import moment from 'moment'
import { isEmpty, isNil } from 'lodash-es'
import { addTradingDays, DateLike, getNextTradingDay, getPrevTradingDay, notNilFilter } from 'utils'
import {
  Collateral,
  Deal,
  DealActions,
  DealDetail,
  Market,
  NegotiationFieldMeta,
  Severity,
  User,
  UserPreference,
  UserStatus,
  ValidationIssue
} from 'api'
import { createInvalidValueValidationIssue, createMissingMandatoryValidationIssue } from 'model'
import { Locale, Translation } from 'locale'
import { add, addBusinessDays, isWeekend } from 'date-fns'

export function getIsBeforeCutOffTime(defaultNotificationDeadlineTime: string, time?: DateLike): boolean {
  const [hours, minutes, seconds] = defaultNotificationDeadlineTime.split(':').map(timePart => parseInt(timePart, 10))
  const compareTime = time ? moment(time).utc() : moment.utc()
  const cutOffTime = compareTime
    .clone()
    .hours(hours)
    .minutes(minutes)
    .seconds(seconds)

  return compareTime.isBefore(cutOffTime)
}

export function getDefaultEffectiveDate(
  platformTradeDate: DateLike,
  isBeforeCutOffTime: boolean,
  defaultNoticePeriod: number,
  holidays: DateLike[],
  maxEffectiveDate: Date | null
) {
  const baseDate = isBeforeCutOffTime ? platformTradeDate : getNextTradingDay(platformTradeDate, holidays)
  const baseDatePlusNoticePeriod = addTradingDays(baseDate, defaultNoticePeriod, holidays)

  if (isNil(maxEffectiveDate)) {
    return baseDatePlusNoticePeriod
  }

  if (moment(baseDatePlusNoticePeriod).isBefore(maxEffectiveDate)) {
    return baseDatePlusNoticePeriod
  }

  return moment(maxEffectiveDate).isBefore(platformTradeDate) ? null : maxEffectiveDate
}

export function getMinEffectiveDate(
  deal: Deal,
  platformTradeDate: DateLike | null,
  isBeforeCutOffTime: boolean,
  evergreenNoticePeriod?: number | null
) {
  const evergreenNoticePeriodDate = evergreenNoticePeriod ? getEvergreenNoticePeriodDate(isBeforeCutOffTime, evergreenNoticePeriod) : null
  const dates = [
    moment(deal.startDate),
    deal.repoInterestStartDate ? moment(deal.repoInterestStartDate) : null,
    platformTradeDate ? moment(platformTradeDate) : null,
    evergreenNoticePeriodDate ? moment(evergreenNoticePeriodDate) : null
  ].filter(notNilFilter)
  return moment.max(dates).toDate()
}

export function getMaxEffectiveDate(
  dealDetail: DealDetail,
  collateral: Collateral | null,
  platformPreMaturityDays: number | null,
  holidays: DateLike[],
  extendMaturityDate: boolean = false
) {
  const endDate = getEarlierOfEndDateAndProductEndDate(dealDetail, collateral, platformPreMaturityDays, extendMaturityDate)

  // Creating a Date object here from the date string as new Date sets the UTC time to 00:00:00 for a date-only string whereas
  // moment sets the local time to 00:00:00 for a date-only string
  return isNil(endDate) ? null : getPrevTradingDay(endDate, holidays)
}

function getEarlierOfEndDateAndProductEndDate(
  dealDetail: DealDetail,
  collateral: Collateral | null,
  platformPreMaturityDays: number | null,
  extendMaturityDate: boolean = false
) {
  const endDate = dealDetail.deal.endDate
  const collateralEndDate = collateral?.couponMaturityDate
  const collateralBasketEndDate = dealDetail.collateralBasket?.basketToDate
  const dealCollateralEndDate = dealDetail.collateral?.couponMaturityDate

  if (isNil(endDate) && isNil(collateralEndDate) && isNil(collateralBasketEndDate) && isNil(dealCollateralEndDate)) {
    return null
  }

  const productEndDate = isNil(collateralEndDate)
    ? moment(collateralBasketEndDate)
    : moment(collateralEndDate).subtract(platformPreMaturityDays ?? 0, 'day')

  if (!isNil(endDate) && !isNil(productEndDate)) {
    return productEndDate.isBefore(endDate) ? productEndDate.toDate() : endDate
  } else if (isNil(endDate) && isNil(collateralEndDate) && !isNil(collateralBasketEndDate)) {
    return productEndDate.toDate()
  }

  const existingCollateralEndDate = getCollateralEndDateOrExtendedEndDate(dealCollateralEndDate, extendMaturityDate)
  const newCollateralEndDate = isNil(endDate) ? productEndDate?.toDate() : moment(endDate).toDate()

  return moment(dealCollateralEndDate).isSameOrBefore(newCollateralEndDate) ? existingCollateralEndDate : newCollateralEndDate
}

function getCollateralEndDateOrExtendedEndDate(endDate: DateLike | null | undefined, extendMaturityDate: boolean) {
  const today = moment().startOf('day')
  const dealEndDate = moment(endDate).toDate()
  const isOnOrPastMaturityDate = moment(today).isSameOrAfter(dealEndDate)
  const extendedDealEndDate = isWeekend(dealEndDate) ? addBusinessDays(dealEndDate, 5) : addBusinessDays(dealEndDate, 4)

  return extendMaturityDate && isOnOrPastMaturityDate ? extendedDealEndDate : dealEndDate
}

function getEvergreenNoticePeriodDate(isBeforeCutOffTime: boolean, evergreenNoticePeriod: number) {
  const today = new Date()
  const evergreenNoticePeriodDate = isBeforeCutOffTime
    ? add(today, { days: evergreenNoticePeriod })
    : add(today, { days: evergreenNoticePeriod + 1 })

  return isWeekend(evergreenNoticePeriodDate) ? addBusinessDays(evergreenNoticePeriodDate, 1) : evergreenNoticePeriodDate
}

export function getServicingDecisionMakerValidationIssues(
  investmentDecisionMaker: string | null,
  executionDecisionMaker: string | null,
  deal: Deal,
  getUser: (userId: string) => User | null,
  idmFieldKey: string = 'investmentDecisionMaker',
  edmFieldKey: string = 'executionDecisionMaker'
) {
  const validationIssues: ValidationIssue[] = [
    isEmpty(investmentDecisionMaker) && deal.market !== Market.US ? createMissingMandatoryValidationIssue(idmFieldKey) : null,
    isEmpty(executionDecisionMaker) && deal.market !== Market.US ? createMissingMandatoryValidationIssue(edmFieldKey) : null
  ].filter(notNilFilter)

  if (investmentDecisionMaker) {
    const idmUser = getUser(investmentDecisionMaker)
    if (idmUser && (idmUser.status !== UserStatus.ACTIVE || !idmUser.canTrade)) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          idmFieldKey,
          Locale.translate(Translation.SC_DECISION_MAKER_NOT_ACTIVE_ERROR_MESSAGE),
          Severity.ERROR
        )
      )
    }
  }

  if (executionDecisionMaker) {
    const edmUser = getUser(executionDecisionMaker)
    if (edmUser && (edmUser.status !== UserStatus.ACTIVE || !edmUser.canTrade)) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          edmFieldKey,
          Locale.translate(Translation.SC_DECISION_MAKER_NOT_ACTIVE_ERROR_MESSAGE),
          Severity.ERROR
        )
      )
    }
  }

  return validationIssues
}

export const createHandleStartConsiderationOverrideUpdate = (
  userPreference: UserPreference,
  dealId: string,
  onUpdateField: typeof DealActions.servicingUpdateAttributes
) => (value: any, field: NegotiationFieldMeta, rawValue?: any) => {
  onUpdateField({
    dealId,
    field,
    updatedValue: value,
    updatedRawValue: rawValue,
    decisionMakerOrientedByNegotiationSide: true,
    meta: { haircutSign: userPreference.haircutSign },
    revertFirst: false
  })
  onUpdateField({
    dealId,
    field: NegotiationFieldMeta.START_CONSIDERATION,
    updatedValue: null,
    updatedRawValue: '',
    decisionMakerOrientedByNegotiationSide: true,
    meta: { haircutSign: userPreference.haircutSign },
    revertFirst: false
  })
}

/**
 * Determines if for every property in a given array of items the property exists
 * this can be later used to hide the column and gain some space if the data is empty
 **/

export const isPropertyEmptyInAll = <T>(items: T[], propertyPath: (item: T) => any): boolean => {
  return items.every(item => {
    const value = propertyPath(item)
    return value === undefined || value === null || value === ''
  })
}
